import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthBrandHeading } from '@/auth/auth-brand-heading';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/auth/auth-store';
import { isBlank, isFlutterEmail } from '@/auth/email-validation';
import { safeLogAuthError } from '@/auth/log-helpers';
import {
  DEV_AUTO_LOGIN_CREDENTIALS,
  DEV_AUTO_LOGIN_ENABLED,
} from '@/auth/dev-auto-login';
import { DEV_PERSONA_LOGIN } from '@/auth/dev-persona-login';
import { track } from '@/shared/lib/analytics';
import { readReturnTo } from '@/auth/return-to';

/**
 * PM login page (Phase 1 of the auth+PM-login un-defer, PROD-885).
 *
 * Wire-shape parity with Flutter `login_signup_module/login`:
 *   - `login_screen.dart` — screen scaffolding + post-success navigation
 *   - `login/body_widgets.dart` — two text fields + isEmail validator
 *   - `login/footer_widgets.dart` — Login button (enabled iff both
 *     fields non-blank) + Forgot Password link
 *   - `login_data_source_impl.dart` — `/connect/token` form-urlencoded body
 *     (already MATCH via `apiLogin` in `auth-api.ts`)
 *   - `login_repository_impl.dart:58-101` — error mapping (401/400/422/404)
 *     reactive-lockout chain on 400 (already MATCH via `mapLoginError` in
 *     `auth-store.ts`)
 *
 * **CheckUserExistence pre-flight is web-skipped** per Flutter
 * `login_bloc.dart:37` (`if (event.isWeb != true)`). React goes straight
 * to `/connect/token`; the 400/401/422/404 cases surface verbatim
 * error strings via the existing global toast pipeline.
 *
 * **GetAppVersionByPlatform** is mounted globally via `<UpgradeAlert />`
 * in `src/app/routes/index.tsx` (PROD-861 #292) — not gated to this page.
 *
 * **Password 5-rule validator** is NOT a login concern (Flutter has
 * `!isBlank` only on the login form). 5-rule validation lives in
 * create-password / reset-password flows (Phases 3b/4/5).
 */
export function PmLoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const login = useAuthStore((s) => s.login);

  // DEV-ONLY: prefill the shared dev test-user credentials so the auto-login
  // effect (and manual retries) can submit without typing. In production
  // `DEV_AUTO_LOGIN_ENABLED` is false and the fields start empty. See
  // `dev-auto-login.ts`.
  const [email, setEmail] = useState(
    DEV_AUTO_LOGIN_ENABLED ? DEV_AUTO_LOGIN_CREDENTIALS.email : '',
  );
  const [password, setPassword] = useState(
    DEV_AUTO_LOGIN_ENABLED ? DEV_AUTO_LOGIN_CREDENTIALS.password : '',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Shared login runner used by both the manual form submit and the DEV
  // auto-login effect. Extracted so the effect doesn't have to synthesize a
  // form event.
  const runLogin = useCallback(
    async (emailToUse: string, passwordToUse: string) => {
      setIsSubmitting(true);
      try {
        await login(emailToUse, passwordToUse);
        // Analytics LOGIN event — Flutter `utils.dart:834` fires
        // `track(MixPanelEvents.LOGIN.name, ...)`. `.name` emits the RAW enum
        // constant, so the production event is "LOGIN" (uppercase), NOT the
        // getEventName()/PascalCase form — confirmed against the live Scrips
        // Practice Management Mixpanel project (existing "LOGIN" event, next
        // to "BILLING"/"CALENDAR"/etc.). Using "LOGIN" merges into that
        // funnel; "Login" would fork a divergent event (the bug this fixes).
        // (Password-reset events DO use getEventName() → title-case, so those
        // stay "Password Reset Initiated" etc. — different code path.)
        // TODO(analytics, Samer review PR #512 §3): Flutter also sends
        // `organization_name` + `current_env` on this event. They're omitted
        // here because the React `UserData` object carries neither (only
        // `organizationId`, which is ALSO registered as a Mixpanel
        // super-property via app/integrations/mixpanel.tsx, so it still
        // attaches). If the analytics pipeline/dashboard depends on
        // `organization_name`/`current_env` on LOGIN, source them (org name
        // from the org query; env from the API-base config) before relying on
        // this event for org-level segmentation. No patient PHI — staff user.
        const u = useAuthStore.getState().user;
        track('LOGIN', {
          user_id: u?.userId,
          user_email: u?.email,
          user_fname: u?.firstName,
          user_lname: u?.lastName,
          organization_id: u?.organizationId,
          app_type: 'Practice Management App',
        });
        // PROD-2366 — `state.from` carries the WHOLE URL the visitor asked
        // for (query string + hash included); `readReturnTo` re-checks it is
        // a same-origin relative path before we navigate to it.
        navigate(readReturnTo(location.state), { replace: true });
      } catch (err) {
        // `auth-store.login()` rethrows a plain `Error` with the
        // Flutter-verbatim user-facing message (`mapLoginError`). Bypass
        // `showServerError` so the verbatim wording survives intact —
        // `showServerError` collapses non-`ApiError` failures to the
        // generic catch-all, which would mask the lockout / wrong-pw
        // messages we just composed.
        safeLogAuthError('PM login', err);
        toast.error(
          err instanceof Error && err.message
            ? err.message
            : 'Login failed. Please try again.',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [login, navigate, location.state],
  );

  // DEV-ONLY auto-login: fire exactly once on mount when enabled and the
  // session is unauthenticated. The ref guard survives StrictMode's
  // double-invoke and re-renders so we never fire two concurrent logins.
  // Disabled entirely in production (`DEV_AUTO_LOGIN_ENABLED` is false).
  const autoLoginFired = useRef(false);
  //
  // A deployed-dev `?devlogin=<persona>` request (dev-persona-login.ts) takes
  // precedence over the default account and fires even when `?login=manual`
  // switched the default off: asking for a persona is an explicit request.
  useEffect(() => {
    if (!DEV_PERSONA_LOGIN && !DEV_AUTO_LOGIN_ENABLED) return;
    if (autoLoginFired.current) return;
    if (status !== 'unauthenticated') return;
    autoLoginFired.current = true;
    if (DEV_PERSONA_LOGIN) {
      void runLogin(DEV_PERSONA_LOGIN.username, DEV_PERSONA_LOGIN.password);
      return;
    }
    void runLogin(
      DEV_AUTO_LOGIN_CREDENTIALS.email,
      DEV_AUTO_LOGIN_CREDENTIALS.password,
    );
  }, [status, runLogin]);

  // Already signed in — bounce to the requested route (or home). Mirrors
  // Flutter `goToHome(...)` post-login navigation. `state.from` is set
  // by the unauthenticated-redirect guard in `App.tsx` when a protected
  // route bounces here.
  if (status === 'authenticated' || status === 'refreshing') {
    return <Navigate to={readReturnTo(location.state)} replace />;
  }

  // Mirrors Flutter `body_widgets.dart:46-58` — email field clears its
  // mirror when invalid (so the button-enable check trips) but the
  // displayed text remains. React: we track the input string, and the
  // submit-eligibility check uses `isFlutterEmail` directly.
  const showEmailError = !isBlank(email) && !isFlutterEmail(email);
  const canSubmit =
    isFlutterEmail(email) && !isBlank(password) && !isSubmitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    await runLogin(email, password);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sidebar px-4">
      <form
        onSubmit={onSubmit}
        className="w-90 max-w-full rounded-lg border border-border-default bg-surface-card p-8 flex flex-col gap-4"
        aria-label="Sign in to Scrips"
      >
        <AuthBrandHeading
          title="Log into your account"
          subtitle="Please enter your login details"
        />

        <div className="flex flex-col gap-2">
          <Label
            htmlFor="pm-login-email"
            className="text-xs font-medium text-text-secondary"
          >
            Email address
          </Label>
          <Input
            id="pm-login-email"
            type="text"
            inputMode="email"
            autoComplete="username"
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={showEmailError || undefined}
            aria-describedby={
              showEmailError ? 'pm-login-email-error' : undefined
            }
            disabled={isSubmitting}
          />
          {showEmailError && (
            <span
              id="pm-login-email-error"
              role="alert"
              className="text-sm text-status-error"
            >
              Email is not valid
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label
            htmlFor="pm-login-password"
            className="text-xs font-medium text-text-secondary"
          >
            Password
          </Label>
          <Input
            id="pm-login-password"
            type="password"
            autoComplete="current-password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <Button type="submit" disabled={!canSubmit} className="mt-2">
          {isSubmitting ? 'Signing in…' : 'Log in'}
        </Button>

        <Link
          to="/forgot-password"
          className="self-center text-sm font-medium text-brand-primary hover:underline"
        >
          Forgot password?
        </Link>

        {/* PROD-929: visible "Sign Up with Access Code" entry from PM
            login. Diverges from Flutter PM, which hardcodes
            `shouldShowSignUpWithAccessCode: false` at `login_screen.dart:233`
            so the footer button at `footer_widgets.dart:87-98` is never
            rendered. Andrew (2026-05-22) explicitly wants the link visible
            on PM — org-admins onboarding staff use this entry to access
            the signup flow rather than only the email-invite link path. */}
        <Link
          to="/signup"
          className="self-center text-sm font-medium text-brand-primary hover:underline"
        >
          Sign up with access code
        </Link>
      </form>
    </div>
  );
}

export default PmLoginPage;
